import { useEffect, useRef, useState } from "react";
import { Topbar } from "../components/AppShell";
import { useHiddenDashboards } from "../lib/hiddenDashboards";

type Props = { onNavigate: (page: string) => void };

const DASHBOARDS = [
  {
    id: "mrr-sandbox",
    title: "MRR Tracking",
    description: "Monthly Recurring Revenue by geography, product, and plan.",
    icon: "ti-coin",
    accent: "",
  },
  {
    id: "demo-funnel",
    title: "Demo Conversion",
    description: "Of demos run, who signed up, activated, and converted to paid.",
    icon: "ti-presentation",
    accent: "",
  },
  {
    id: "signup-funnel",
    title: "Sign-up Activation",
    description: "Of users who signed up, what % activate and which features predict paid conversion.",
    icon: "ti-chart-dots",
    accent: "",
  },
  {
    id: "attributions",
    title: "Attributions",
    description: "Where sign-ups come from and what they convert to.",
    icon: "ti-arrows-split",
    accent: "",
  },
  {
    id: "marloo-wau",
    title: "Weekly Active Users",
    description: "WAU by feature, plus per-user and per-company entity views.",
    icon: "ti-users",
    accent: "",
  },
  {
    id: "onboarding-baseline",
    title: "Onboarding Baseline",
    description: "The locked 30-180 day signup cohort: activation funnel, feature reach, time to first summary.",
    icon: "ti-flag",
    accent: "",
  },
  {
    id: "churn-leading-indicators",
    title: "Churn leading indicators",
    description: "Early warning signals across meetings, transcripts, summaries, and Ask Marloo.",
    icon: "ti-alert-triangle",
    accent: "",
  },
  {
    id: "llm-usage",
    title: "LLM Usage",
    description: "Spend, throughput, and latency across every LLM call by feature, model, and customer.",
    icon: "ti-robot",
    accent: "",
  },
  {
    id: "document-qa",
    title: "Document QA",
    description: "Three-pane QA view: licensee template, document output, raw prompt (Stage QA company).",
    icon: "ti-clipboard-check",
    accent: "",
  },
];

export function Home({ onNavigate }: Props) {
  const { isHidden, toggle } = useHiddenDashboards();
  const [showHidden, setShowHidden] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const visible = DASHBOARDS.filter((d) => showHidden || !isHidden(d.id));
  const hiddenCount = DASHBOARDS.filter((d) => isHidden(d.id)).length;

  return (
    <>
      <Topbar crumbs={["Dashboards", "Home"]} />
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Dashboards</h1>
            <p className="lede">
              Everything the growth and product teams look at, in one place. Data is pulled
              read-only from the Marloo production database.
            </p>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: 12,
            gap: 12,
          }}
        >
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Dashboards</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {hiddenCount > 0 ? (
              <button
                type="button"
                onClick={() => setShowHidden((v) => !v)}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  fontSize: 12,
                  color: "var(--fg-2)",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <i className={`ti ${showHidden ? "ti-eye-off" : "ti-eye"}`} />
                {showHidden ? `Hide hidden (${hiddenCount})` : `Show hidden (${hiddenCount})`}
              </button>
            ) : null}
            <span style={{ fontSize: 12, color: "var(--fg-3)" }}>
              {visible.length} of {DASHBOARDS.length}
            </span>
          </div>
        </div>

        <div className="home-grid">
          {visible.map((d) => {
            const hidden = isHidden(d.id);
            return (
              <DashCard
                key={d.id}
                id={d.id}
                title={d.title}
                description={d.description}
                icon={d.icon}
                accent={d.accent}
                hidden={hidden}
                menuOpen={openMenu === d.id}
                onOpenMenu={(open) => setOpenMenu(open ? d.id : null)}
                onToggleHidden={() => {
                  toggle(d.id);
                  setOpenMenu(null);
                }}
                onOpen={() => onNavigate(d.id)}
              />
            );
          })}
        </div>
      </div>
    </>
  );
}

type CardProps = {
  id: string;
  title: string;
  description: string;
  icon: string;
  accent: string;
  hidden: boolean;
  menuOpen: boolean;
  onOpenMenu: (open: boolean) => void;
  onToggleHidden: () => void;
  onOpen: () => void;
};

function DashCard({
  title,
  description,
  icon,
  accent,
  hidden,
  menuOpen,
  onOpenMenu,
  onToggleHidden,
  onOpen,
}: CardProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onOpenMenu(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen, onOpenMenu]);

  return (
    <div
      role="button"
      tabIndex={0}
      className={`dash-card ${accent}`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{ position: "relative", opacity: hidden ? 0.62 : 1 }}
    >
      <div
        ref={menuRef}
        style={{ position: "absolute", top: 10, right: 10 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenMenu(!menuOpen);
          }}
          title="Options"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 4,
            borderRadius: 4,
            color: "var(--fg-3)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i className="ti ti-dots" style={{ fontSize: 16 }} />
        </button>
        {menuOpen ? (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              right: 0,
              background: "var(--white)",
              border: "1px solid var(--border-default)",
              borderRadius: 6,
              boxShadow: "var(--shadow-md)",
              minWidth: 160,
              padding: 4,
              zIndex: 10,
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleHidden();
              }}
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                textAlign: "left",
                padding: "8px 10px",
                borderRadius: 4,
                cursor: "pointer",
                fontSize: 13,
                color: "var(--fg-1)",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <i className={`ti ${hidden ? "ti-eye" : "ti-eye-off"}`} />
              {hidden ? "Show dashboard" : "Hide dashboard"}
            </button>
          </div>
        ) : null}
      </div>

      <div className="icn">
        <i className={`ti ${icon}`} />
      </div>
      <h3 style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {title}
        {hidden ? (
          <i
            className="ti ti-eye-off"
            title="Hidden"
            style={{ fontSize: 13, color: "var(--fg-3)" }}
          />
        ) : null}
      </h3>
      <p>{description}</p>
      <div className="cta">
        <span>Open</span>
        <i className="ti ti-arrow-right" />
      </div>
    </div>
  );
}
